import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import React, { useContext, useState } from 'react';

import AppContext from '@/contexts/appContext';
import PersonAvailabilityRepository from '@/repositories/PersonAvailabilityRepository';
import { TimeInterval } from '@/types/personAvailability';
import Button from '@/ui/components/Button';
import InputField from '@/ui/components/Form/InputField/InputField';
import {
  formatDateRange,
  generateDateRange,
  getCurrentDate,
  groupConsecutiveDates
} from '@/utils/dateAvailability';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { convertTo24Hour, validateNoOverlappingIntervals } from '@/utils/timeFormat';
import { hasPermission, PEOPLE_AVAILABILITY_CHANGE } from '@/utils/userPermission';

import TimeIntervalInput from '../TimeIntervalInput';

import type { DateRuleGroup, Props } from './types';

const DateSpecificHoursSection: React.FC<Props> = ({
  rules,
  availabilityId,
  personId,
  timezone,
  onUpdate
}) => {
  const { addMessage, permissions } = useContext(AppContext);
  const canMutate = hasPermission({ permissions, requiredPermission: PEOPLE_AVAILABILITY_CHANGE });

  const [isAdding, setIsAdding] = useState(false);
  const [newDate, setNewDate] = useState(getCurrentDate());
  const [newDateTo, setNewDateTo] = useState('');
  const [newIntervals, setNewIntervals] = useState<TimeInterval[]>([
    { from: '9:00am', to: '5:00pm' }
  ]);
  const [editingIntervals, setEditingIntervals] = useState<{ [date: string]: TimeInterval[] }>({});
  const [intervalErrors, setIntervalErrors] = useState<{ [date: string]: string | null }>({});
  const availabilityRepo = PersonAvailabilityRepository();

  const dateGroups = groupConsecutiveDates(rules);

  const getIntervalsForGroup = (group: DateRuleGroup): TimeInterval[] => {
    if (editingIntervals[group.startDate]) {
      return editingIntervals[group.startDate];
    }
    return group.intervals;
  };

  // Group handlers - apply changes to all dates in the group
  const handleAddIntervalForGroup = (group: DateRuleGroup) => {
    const currentIntervals = getIntervalsForGroup(group);
    const updatedIntervals = [...currentIntervals, { from: '9:00am', to: '5:00pm' }];
    setEditingIntervals({
      ...editingIntervals,
      [group.startDate]: updatedIntervals
    });
  };

  const handleRemoveIntervalForGroup = async (group: DateRuleGroup, index: number) => {
    const currentIntervals = getIntervalsForGroup(group);
    const newIntervals = currentIntervals.filter((_, i) => i !== index);

    setEditingIntervals({
      ...editingIntervals,
      [group.startDate]: newIntervals
    });

    await saveGroupRules(group, newIntervals);
  };

  const handleIntervalChangeForGroup = async (
    group: DateRuleGroup,
    index: number,
    field: 'from' | 'to',
    value: string
  ) => {
    const currentIntervals = getIntervalsForGroup(group);
    const newIntervals = [...currentIntervals];
    newIntervals[index] = { ...newIntervals[index], [field]: value };

    setEditingIntervals({
      ...editingIntervals,
      [group.startDate]: newIntervals
    });

    await saveGroupRules(group, newIntervals);
  };

  const saveGroupRules = async (group: DateRuleGroup, intervals: TimeInterval[]) => {
    // Validate no overlapping intervals
    const overlapError = validateNoOverlappingIntervals(intervals);
    group.dates.forEach((date) => {
      setIntervalErrors((prev) => ({ ...prev, [date]: overlapError }));
    });
    if (overlapError) {
      return false;
    }

    // Clear errors for all dates in group
    setIntervalErrors((prev) => {
      const next = { ...prev };
      group.dates.forEach((date) => delete next[date]);
      return next;
    });

    // Remove all dates in the group from rules
    const otherRules = rules.filter((r) => !group.dates.includes(r.date || ''));

    // Convert intervals to 24-hour format for API
    const formattedIntervals = intervals.map((interval) => ({
      from: convertTo24Hour(interval.from),
      to: convertTo24Hour(interval.to)
    }));

    // Create rules for each date in the group with the same intervals
    const newRules = group.dates.map((date) => ({
      type: 'date' as const,
      date,
      intervals: formattedIntervals
    }));

    const updatedRules = [...otherRules, ...newRules];

    try {
      if (availabilityId) {
        await availabilityRepo.update({
          id: availabilityId,
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: updatedRules
          }
        });
      } else {
        await availabilityRepo.create({
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: updatedRules
          }
        });
      }
      onUpdate();
      return true;
    } catch (error) {
      const messages = mapAxiosErrorMessages(error as AxiosError);
      messages.forEach((msg) => addMessage(msg.level, msg.text));
      return false;
    }
  };

  const handleDeleteGroup = async (group: DateRuleGroup) => {
    // Remove all dates in the group
    const otherRules = rules.filter((r) => !group.dates.includes(r.date || ''));

    try {
      if (availabilityId) {
        await availabilityRepo.update({
          id: availabilityId,
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: otherRules
          }
        });
      } else {
        await availabilityRepo.create({
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: otherRules
          }
        });
      }
      onUpdate();
      setIntervalErrors((prev) => {
        const next = { ...prev };
        group.dates.forEach((date) => delete next[date]);
        return next;
      });
    } catch (error) {
      const messages = mapAxiosErrorMessages(error as AxiosError);
      messages.forEach((msg) => addMessage(msg.level, msg.text));
    }
  };

  const handleAddNewDate = async () => {
    if (!newDate) return;

    // Generate all dates in the range
    const datesToAdd = generateDateRange(newDate, newDateTo);

    // Validate no overlapping intervals
    const overlapError = validateNoOverlappingIntervals(newIntervals);
    if (overlapError) {
      datesToAdd.forEach((date) => {
        setIntervalErrors((prev) => ({ ...prev, [date]: overlapError }));
      });
      return;
    }

    // Prevent adding availability for dates in the past
    if (newDate < getCurrentDate()) {
      setIntervalErrors((prev) => ({
        ...prev,
        [newDate]: 'Cannot set availability for dates in the past'
      }));
      return;
    }

    // Filter out any existing rules for dates in our range
    const otherRules = rules.filter((r) => !datesToAdd.includes(r.date || ''));

    // Convert intervals to 24-hour format for API
    const formattedIntervals = newIntervals.map((interval) => ({
      from: convertTo24Hour(interval.from),
      to: convertTo24Hour(interval.to)
    }));

    // Create a rule for each date in the range
    const newRules = datesToAdd.map((date) => ({
      type: 'date' as const,
      date,
      intervals: formattedIntervals
    }));

    const updatedRules = [...otherRules, ...newRules];

    try {
      if (availabilityId) {
        await availabilityRepo.update({
          id: availabilityId,
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: updatedRules
          }
        });
      } else {
        await availabilityRepo.create({
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: updatedRules
          }
        });
      }
      onUpdate();
      setIsAdding(false);
      setNewDate(getCurrentDate());
      setNewDateTo('');
      setNewIntervals([{ from: '9:00am', to: '5:00pm' }]);
    } catch (error) {
      const messages = mapAxiosErrorMessages(error as AxiosError);
      messages.forEach((msg) => addMessage(msg.level, msg.text));
    }
  };

  return (
    <div>
      <h2 className="flex items-center gap-2 text-lg font-semibold mb-4">Date-specific hours</h2>
      <p className="text-sm text-gray-600 mb-4">
        Adjust hours for specific days. Remove all time intervals to mark dates as unavailable
        (e.g., vacation).
      </p>

      <div className="space-y-3">
        {dateGroups.length === 0 && !isAdding && (
          <p className="text-sm text-gray-500 p-3 text-center">No date-specific hours set</p>
        )}

        {dateGroups.map((group) => {
          const intervals = getIntervalsForGroup(group);
          const hasError = group.dates.some((date) => intervalErrors[date]);
          const errorMessage = group.dates.map((date) => intervalErrors[date]).find((err) => err);

          return (
            <div key={group.startDate} className="p-3 bg-gray-50 rounded">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900">{formatDateRange(group)}</p>
                  {group.dates.length > 1 && (
                    <p className="text-xs text-gray-500">{group.dates.length} days</p>
                  )}
                </div>
                <Button
                  onClick={() => handleDeleteGroup(group)}
                  aria-label="Delete date-specific hours">
                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                </Button>
              </div>
              <div className="space-y-2">
                {intervals.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Unavailable</p>
                ) : (
                  intervals.map((interval, idx) => (
                    <TimeIntervalInput
                      key={idx}
                      interval={interval}
                      onFromChange={(value) =>
                        handleIntervalChangeForGroup(group, idx, 'from', value)
                      }
                      onToChange={(value) => handleIntervalChangeForGroup(group, idx, 'to', value)}
                      onRemove={() => handleRemoveIntervalForGroup(group, idx)}
                    />
                  ))
                )}
                {hasError && <p className="text-xs text-red-600">{errorMessage}</p>}
                <Button
                  onClick={() => handleAddIntervalForGroup(group)}
                  ariaLabel="Add time interval">
                  <FontAwesomeIcon icon={faPlus} className="text-xs" />
                  <span>Add time</span>
                </Button>
              </div>
            </div>
          );
        })}

        {isAdding && (
          <div className="p-3 bg-gray-50 rounded border-2 border-blue-300">
            <div className="mb-3 grid grid-cols-2 gap-3">
              <InputField
                type="date"
                value={newDate}
                min={getCurrentDate()}
                onChange={(e) => {
                  const nextDate = e.target.value;
                  setIntervalErrors((prev) => {
                    const next = { ...prev };
                    delete next[newDate];
                    delete next[nextDate];
                    return next;
                  });
                  setNewDate(nextDate);
                }}
                id="new-date"
                label="From Date"
              />
              <InputField
                type="date"
                value={newDateTo}
                onChange={(e) => setNewDateTo(e.target.value)}
                id="new-date-to"
                label="To Date (optional)"
              />
            </div>

            <div className="space-y-2 mb-3">
              {newIntervals.map((interval, idx) => (
                <TimeIntervalInput
                  key={idx}
                  interval={interval}
                  onFromChange={(value) => {
                    const updated = [...newIntervals];
                    updated[idx] = { ...updated[idx], from: value };
                    setNewIntervals(updated);
                  }}
                  onToChange={(value) => {
                    const updated = [...newIntervals];
                    updated[idx] = { ...updated[idx], to: value };
                    setNewIntervals(updated);
                  }}
                  onRemove={() => {
                    setNewIntervals(newIntervals.filter((_, i) => i !== idx));
                  }}
                />
              ))}
              {intervalErrors[newDate] && (
                <p className="text-xs text-red-600">{intervalErrors[newDate]}</p>
              )}
              <Button
                onClick={() => setNewIntervals([...newIntervals, { from: '9:00am', to: '5:00pm' }])}
                ariaLabel="Add time interval">
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                <span>Add time</span>
              </Button>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddNewDate} color="blue" size="sm" className="flex-1">
                Save
              </Button>
              <Button
                onClick={() => {
                  setIsAdding(false);
                  setNewDate(getCurrentDate());
                  setNewDateTo('');
                  setNewIntervals([{ from: '9:00am', to: '5:00pm' }]);
                  setIntervalErrors((prev) => {
                    const next = { ...prev };
                    delete next[newDate];
                    return next;
                  });
                }}
                color="white"
                size="sm"
                className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {canMutate && !isAdding && (
        <Button
          onClick={() => setIsAdding(true)}
          color="white"
          className="mt-4 w-full border border-dashed border-gray-300 text-gray-700 hover:border-gray-400">
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Date
        </Button>
      )}
    </div>
  );
};

export default React.memo(DateSpecificHoursSection);
